import { useState, useMemo, useEffect } from "react"
import {
  Search,
  FolderOpen,
  Plus,
  Pencil,
  MessageSquare,
  MapPin,
  FileText,
  Copy,
  ChevronDown,
  ChevronUp,
  Sparkles,
  User,
  Image,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Eye,
  EyeOff,
  Euro,
} from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { Badge } from "../components/ui/badge"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../components/ui/dialog"
import { useLogs } from "../components/LogProvider"
import { useLocale } from "../components/LocaleProvider"
import { toast } from "../components/ui/toast"
import { getUserColor } from "../lib/utils"
import { useClients, useCreateClient, useUpdateClient } from "../lib/queries"
import { Switch } from "../components/ui/switch"
import { api } from "../lib/api"
import { useAuth } from "../components/AuthProvider"
import type { Client, GeneratedProfile, AdditionalPerson } from "../types"
import propImg from "../img/prop.jpg"
import type { TranslationKey } from "../i18n/translations"

const DOCUMENTS_OPTIONS = [
  "Reisepass",
  "Aufenthaltstitel",
  "Meldebestätigung",
  "WBS",
  "Dringlichkeitsbestätigung",
  "Jobcenter Bescheid",
  "Arbeitsvertrag",
  "Kontoauszüge (3 Monate)",
  "Korrespondenz Jobcenter",
  "Korrespondenz Sozialamt",
  "Korrespondenz Fachstelle",
  "Korrespondenz Vermieter",
  "Wohnungen Bewerbungen/Absagen",
  "Sonstige Wohnunterlagen",
] as const

const DOC_T_KEY: Record<string, TranslationKey> = {
  Reisepass: "doc_reisepass",
  Aufenthaltstitel: "doc_aufenthaltstitel",
  Meldebestätigung: "doc_meldebestaetigung",
  WBS: "doc_wbs",
  Dringlichkeitsbestätigung: "doc_dringlichkeitsbestaetigung",
  "Jobcenter Bescheid": "doc_jobcenter_bescheid",
  Arbeitsvertrag: "doc_arbeitsvertrag",
  "Kontoauszüge (3 Monate)": "doc_kontoauszuege",
  "Korrespondenz Jobcenter": "doc_korrespondenz_jobcenter",
  "Korrespondenz Sozialamt": "doc_korrespondenz_sozialamt",
  "Korrespondenz Fachstelle": "doc_korrespondenz_fachstelle",
  "Korrespondenz Vermieter": "doc_korrespondenz_vermieter",
  "Wohnungen Bewerbungen/Absagen": "doc_wohnungen_bewerbungen",
  "Sonstige Wohnunterlagen": "doc_sonstige_wohnunterlagen",
}

const LOCATIONS = [
  "Whole Hamburg",
  "Allermöhe",
  "Alsterdorf",
  "Altengamme",
  "Altenwerder",
  "Altona",
  "Altona-Altstadt",
  "Altona-Nord",
  "Altstadt",
  "Bahrenfeld",
  "Barmbek-Nord",
  "Barmbek-Süd",
  "Bergedorf",
  "Bergstedt",
  "Billbrook",
  "Billstedt",
  "Billwerder",
  "Blankenese",
  "Borgfelde",
  "Bramfeld",
  "Cranz",
  "Curslack",
  "Dulsberg",
  "Duvenstedt",
  "Eidelstedt",
  "Eilbek",
  "Eimsbüttel",
  "Eißendorf",
  "Eppendorf",
  "Farmsen",
  "Farmsen-Berne",
  "Finkenwerder",
  "Francop",
  "Fuhlsbüttel",
  "Groß Borstel",
  "Groß Flottbek",
  "Gut Moor",
  "HafenCity",
  "Hamburg",
  "Hamburg-Altstadt",
  "Hamburg-Mitte",
  "Hamburg-Nord",
  "Hamm",
  "Hamm-Mitte",
  "Hamm-Nord",
  "Hamm-Süd",
  "Hammerbrook",
  "Harburg",
  "Harvestehude",
  "Hausbruch",
  "Heimfeld",
  "Hoheluft-Ost",
  "Hoheluft-West",
  "Hohenfelde",
  "Horn",
  "Hummelsbüttel",
  "Iserbrook",
  "Jenfeld",
  "Kirchdorf",
  "Kirchdorf Süd",
  "Kirchwerder",
  "Kleiner Grasbrook",
  "Langenbek",
  "Langenhorn",
  "Lemsahl-Mellingstedt",
  "Lohbrügge",
  "Lokstedt",
  "Lurup",
  "Marienthal",
  "Marmstorf",
  "Meiendorf",
  "Moorburg",
  "Moorfleet",
  "Mümmelmannsberg",
  "Nettelnburg",
  "Neuallermöhe",
  "Neuenfelde",
  "Neuengamme",
  "Neugraben-Fischbek",
  "Neuland",
  "Neustadt",
  "Neuwiedenthal",
  "Niendorf",
  "Nienstedten",
  "Ochsenwerder",
  "Ohlsdorf",
  "Oldenfelde",
  "Osdorf",
  "Othmarschen",
  "Ottensen",
  "Poppenbüttel",
  "Rahlstedt",
  "Reitbrook",
  "Rissen",
  "Rothenburgsort",
  "Rotherbaum",
  "Rönneburg",
  "Sankt Georg",
  "Sankt Georg (Stadtteil)",
  "Sasel",
  "Schnelsen",
  "Sinstorf",
  "Spadenland",
  "St. Georg",
  "St. Pauli",
  "Steilshoop",
  "Steinwerder",
  "Stellingen",
  "Sternschanze",
  "Sülldorf",
  "Tatenberg",
  "Tonndorf",
  "Uhlenhorst",
  "Veddel",
  "Vier- und Marschlande",
  "Volksdorf",
  "Waltershof",
  "Wandsbek",
  "Wellingsbüttel",
  "Wilhelmsburg",
  "Wilstorf",
  "Winterhude",
  "Wohldorf-Ohlstedt",
] as const

const SUBSCRIPTION_OPTIONS = ["3 months", "6 months", "1 year", "Premium"] as const

const SUB_T_KEY: Record<string, TranslationKey> = {
  "3 months": "sub_3m",
  "6 months": "sub_6m",
  "1 year": "sub_1y",
  Premium: "sub_premium",
}

const SUBSCRIPTION_MONTHS: Record<string, number> = {
  "3 months": 3,
  "6 months": 6,
  "1 year": 12,
}

const DISABLED_REASON_OPTIONS = [
  "Package ended",
  "Client decision",
  "Settled",
  "Other",
] as const

const DISABLED_REASON_T_KEY: Record<string, TranslationKey> = {
  "Package ended": "reason_package_ended",
  "Client decision": "reason_client_decision",
  Settled: "reason_settled",
  Other: "reason_other",
}

const HIDE_SENSITIVE_STORAGE_KEY = "clients_hide_sensitive"

const DEFAULT_SUBSCRIPTION = "3 months"

function getPartnershipDuration(createdAt: string): string {
  const created = new Date(createdAt)
  const now = new Date()
  const diffMs = now.getTime() - created.getTime()
  if (diffMs < 0) return ""
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  if (diffDays < 30) return `${diffDays}D ${diffHours}h`
  const months = Math.floor(diffDays / 30)
  const days = diffDays % 30
  return `${months}M ${days}D`
}

const emptyForm: Omit<Client, "id"> = {
  fullName: "",
  phone: "",
  folderLink: "",
  operator: "",
  comment: "",
  price: "",
  rooms: "",
  sqft: "",
  location: "",
  documents: "",
  subscription: "",
  subscriptionDuration: 0,
  subscriptionStart: "",
  subscriptionEnd: "",
  color: "",
  sex: null,
  birthName: "",
  birthPlaceCity: "",
  birthPlaceCountry: "",
  street: "",
  houseNumber: "",
  plz: "",
  city: "",
  country: "",
  profession: "",
  employer: "",
  monthlyNetIncome: null,
  additionalTenantInfo: false,
  additionalAdults: false,
  additionalMinors: false,
  additionalPersons: [],
  enabled: true,
  disabledReason: "",
  createdAt: "",
}

type FieldConfig = {
  key: "price" | "rooms" | "sqft"
  label: string
  placeholder: string[]
  single?: boolean
  icon?: React.ComponentType<{ className?: string }>
}

function MultiSelectDropdown({
  options,
  value,
  onChange,
  placeholder,
  icon: Icon = FileText,
  renderLabel,
}: {
  options: readonly string[]
  value: string
  onChange: (joined: string) => void
  placeholder: string
  icon?: React.ComponentType<{ className?: string }>
  renderLabel?: (item: string) => React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const selected = value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)

  const toggle = (doc: string) => {
    const next = selected.includes(doc) ? selected.filter((d) => d !== doc) : [...selected, doc]
    onChange(next.join(", "))
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className={selected.length === 0 ? "text-muted-foreground" : ""}>
          {selected.length === 0 ? placeholder : `${selected.length}/${options.length}`}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-md border border-input bg-background shadow-lg">
            {options.map((doc) => {
              const isSelected = selected.includes(doc)
              return (
                <label
                  key={doc}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-accent ${
                    isSelected ? "bg-blue-50 dark:bg-blue-950" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(doc)}
                    className="h-4 w-4 rounded border-input accent-blue-500"
                  />
                  <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  {renderLabel ? renderLabel(doc) : doc}
                </label>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function Clients() {
  const { user } = useAuth()
  const isAdmin = user?.role === "ADMIN"
  const { data: clientsData, isLoading } = useClients()
  const data: Client[] = clientsData ?? []
  const createMut = useCreateClient()
  const updateMut = useUpdateClient()

  const { logAction } = useLogs()
  const { t } = useLocale()
  const [search, setSearch] = useState("")
  const [operatorFilter, setOperatorFilter] = useState("")
  const [subscriptionEndSort, setSubscriptionEndSort] = useState<"none" | "asc" | "desc">("none")
  const [hideSensitive, setHideSensitive] = useState(
    () => sessionStorage.getItem(HIDE_SENSITIVE_STORAGE_KEY) === "true",
  )

  useEffect(() => {
    sessionStorage.setItem(HIDE_SENSITIVE_STORAGE_KEY, String(hideSensitive))
  }, [hideSensitive])

  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState<number | null>(null)

  const [commentOpen, setCommentOpen] = useState(false)
  const [commentClientId, setCommentClientId] = useState<number | null>(null)
  const [commentText, setCommentText] = useState("")

  const [propImgOpen, setPropImgOpen] = useState(false)
  const [propImgZoom, setPropImgZoom] = useState(1)

  const [profilesOpen, setProfilesOpen] = useState(false)
  const [profilesClientId, setProfilesClientId] = useState<number | null>(null)
  const [profilesData, setProfilesData] = useState<GeneratedProfile[]>([])
  const [profilesLoading, setProfilesLoading] = useState(false)

  const [personalOpen, setPersonalOpen] = useState(false)
  const [personalClientId, setPersonalClientId] = useState<number | null>(null)
  const [personalForm, setPersonalForm] = useState({
    sex: null as "MALE" | "FEMALE" | null,
    birthName: "",
    birthPlaceCity: "",
    birthPlaceCountry: "",
    profession: "",
    employer: "",
    monthlyNetIncome: null as number | null,
    street: "",
    houseNumber: "",
    plz: "",
    city: "Hamburg",
    country: "Deutschland",
    additionalTenantInfo: false,
    additionalAdults: false,
    additionalMinors: false,
    additionalPersons: [] as AdditionalPerson[],
  })

  const hasIncompletePersonalData = (record: any) => {
    const requiredFields = [
      record.sex,
      record.birthName,
      record.birthPlaceCity,
      record.birthPlaceCountry,
      record.profession,
      record.employer,
      record.monthlyNetIncome,
      record.street,
      record.houseNumber,
      record.plz,
      record.city,
      record.country,
    ]
    return requiredFields.some((v) => v === null || v === undefined || v === "")
  }

  const operatorOptions = useMemo(
    () =>
      Array.from(new Set(data.map((r) => r.operator).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b),
      ),
    [data],
  )

  const filtered = useMemo(
    () =>
      data.filter((row) => {
        if (operatorFilter && row.operator !== operatorFilter) return false
        return Object.values(row).some((v) =>
          String(v).toLowerCase().includes(search.toLowerCase()),
        )
      }),
    [data, search, operatorFilter],
  )

  const sorted = useMemo(() => {
    if (subscriptionEndSort === "none") return filtered
    return [...filtered].sort((a, b) => {
      const aTime = a.subscriptionEnd ? new Date(a.subscriptionEnd).getTime() : 0
      const bTime = b.subscriptionEnd ? new Date(b.subscriptionEnd).getTime() : 0
      if (aTime === bTime) return 0
      if (aTime === 0) return 1
      if (bTime === 0) return -1
      return subscriptionEndSort === "asc" ? aTime - bTime : bTime - aTime
    })
  }, [filtered, subscriptionEndSort])

  const enabledClients = useMemo(() => sorted.filter((r) => r.enabled !== false), [sorted])
  const disabledClients = useMemo(() => sorted.filter((r) => r.enabled === false), [sorted])

  const openAdd = () => {
    setEditingId(null)
    const months = SUBSCRIPTION_MONTHS[DEFAULT_SUBSCRIPTION] ?? 0
    const today = new Date().toISOString().slice(0, 10)
    const end =
      months > 0
        ? new Date(Date.now() + months * 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
        : ""
    setForm({
      ...emptyForm,
      operator: user?.fullName ?? "",
      subscription: DEFAULT_SUBSCRIPTION,
      subscriptionDuration: months,
      subscriptionStart: today,
      subscriptionEnd: end,
    })
    setOpen(true)
  }

  const openEdit = (id: number) => {
    const record = data.find((c) => c.id === id)
    if (!record) return
    setEditingId(id)
    const { id: _id, ...rest } = record
    setForm(rest)
    setOpen(true)
  }

  const openComment = (id: number) => {
    const record = data.find((c) => c.id === id)
    if (!record) return
    setCommentClientId(id)
    setCommentText(record.comment)
    setCommentOpen(true)
  }

  const openProfiles = async (id: number) => {
    const record = data.find((c) => c.id === id)
    if (!record) return
    setProfilesClientId(id)
    setProfilesData([])
    setProfilesOpen(true)
    setProfilesLoading(true)
    try {
      const result = await api.generateProfiles(id)
      setProfilesData(result.profiles)
    } catch (err: any) {
      toast(err.message || "Failed to generate profiles", "error")
      setProfilesOpen(false)
    } finally {
      setProfilesLoading(false)
    }
  }

  const openPersonal = (id: number) => {
    const record = data.find((c) => c.id === id)
    if (!record) return
    setPersonalClientId(id)
    setPersonalForm({
      sex: record.sex,
      birthName: record.birthName,
      birthPlaceCity: record.birthPlaceCity,
      birthPlaceCountry: record.birthPlaceCountry,
      profession: record.profession,
      employer: record.employer,
      monthlyNetIncome: record.monthlyNetIncome,
      street: record.street,
      houseNumber: record.houseNumber,
      plz: record.plz,
      city: record.city || "Hamburg",
      country: record.country || "Deutschland",
      additionalTenantInfo: record.additionalTenantInfo,
      additionalAdults: record.additionalAdults,
      additionalMinors: record.additionalMinors,
      additionalPersons: (record.additionalPersons || []).map((p) => ({
        type: p.type,
        firstName: p.firstName,
        lastName: p.lastName,
        birthday: p.birthday,
        salutation: p.salutation || "",
        birthName: p.birthName || "",
        birthPlaceCity: p.birthPlaceCity || "",
        street: p.street || "",
        houseNumber: p.houseNumber || "",
        plz: p.plz || "",
        city: p.city || "",
        country: p.country || "",
        email: p.email || "",
        phone: p.phone || "",
        profession: p.profession || "",
        employer: p.employer || "",
        monthlyNetIncome: p.monthlyNetIncome ?? null,
      })),
    })
    setPersonalOpen(true)
  }

  const handlePersonalSubmit = () => {
    if (personalClientId === null) return
    updateMut.mutate({ id: personalClientId, dto: personalForm })
    setPersonalOpen(false)
    setPersonalClientId(null)
  }

  const handleCommentSubmit = () => {
    if (commentClientId === null) return
    updateMut.mutate({ id: commentClientId, dto: { comment: commentText } })
    setCommentOpen(false)
    setCommentClientId(null)
    setCommentText("")
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.enabled && !form.disabledReason) {
      toast(t("err_disabled_reason_required"), "error")
      return
    }
    const dto = {
      ...form,
      disabledReason: form.enabled ? "" : form.disabledReason,
    }
    if (editingId !== null) {
      updateMut.mutate({ id: editingId, dto })
    } else {
      createMut.mutate(dto)
      logAction("clients", "add", `Added client ${form.fullName}`, form.fullName)
    }
    setOpen(false)
    setForm(emptyForm)
    setEditingId(null)
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("search_clients")}
              className="h-10 w-full max-w-sm rounded-md border border-input bg-background pl-10 pr-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          {isAdmin && operatorOptions.length > 0 && (
            <select
              value={operatorFilter}
              onChange={(e) => setOperatorFilter(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t("filter_all_operators")}</option>
              {operatorOptions.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex items-center  gap-3">
          <button
            onClick={() => setHideSensitive((v) => !v)}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors"
            title={hideSensitive ? t("btn_show_sensitive") : t("btn_hide_sensitive")}
          >
            {hideSensitive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {hideSensitive ? t("btn_show_sensitive") : t("btn_hide_sensitive")}
          </button>
          <button
            onClick={() => setPropImgOpen(true)}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors"
          >
            <Image className="h-4 w-4" />
            SAGA requirements
          </button>
          <Dialog
            open={open}
            onOpenChange={(v) => {
              if (!v) {
                setOpen(false)
                setEditingId(null)
              } else {
                setOpen(true)
              }
            }}
          >
            <DialogTrigger asChild>
              <button
                onClick={openAdd}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <Plus className="h-4 w-4" />
                {t("btn_add_record")}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-7xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingId !== null ? t("dialog_edit_client") : t("dialog_add_client")}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div className="space-y-3">
                    {(["fullName", "phone", "folderLink"] as const).map((field) => (
                      <div key={field} className="space-y-1.5">
                        <label className="text-sm font-medium capitalize">
                          {t(
                            field === "fullName"
                              ? "form_full_name"
                              : field === "folderLink"
                                ? "form_folder_link"
                                : (`form_${field}` as TranslationKey),
                          )}
                        </label>
                        <input
                          value={form[field]}
                          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                          placeholder={field === "folderLink" ? t("placeholder_drive") : field}
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          required
                        />
                      </div>
                    ))}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t("form_color")}</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.color || getUserColor(form.fullName)}
                          onChange={(e) => setForm({ ...form, color: e.target.value })}
                          className="h-9 w-12 rounded-md border border-input bg-background cursor-pointer p-0.5"
                        />
                        <input
                          value={form.color}
                          onChange={(e) => setForm({ ...form, color: e.target.value })}
                          placeholder={getUserColor(form.fullName)}
                          className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm font-mono ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      {(
                        [
                          {
                            key: "price" as const,
                            label: t("form_price"),
                            placeholder: ["Max"],
                            single: true,
                            icon: Euro,
                          },
                          {
                            key: "rooms" as const,
                            label: t("form_rooms"),
                            placeholder: ["Min (opt)", "Max"],
                          },
                          {
                            key: "sqft" as const,
                            label: t("form_sqft"),
                            placeholder: ["Min m² (opt)", "Max m²"],
                          },
                        ] as FieldConfig[]
                      ).map(({ key, label, placeholder, single, icon: Icon }) => {
                        const [min = "", max = ""] = (form[key] || "").split("-")
                        const value = single ? form[key] || "" : max || min
                        return (
                          <div key={key} className="space-y-1.5">
                            <label className="text-sm font-medium">{label}</label>
                            {single ? (
                              <div className="relative">
                                <input
                                  value={value}
                                  onChange={(e) => {
                                    const v = e.target.value.replace(/[^0-9]/g, "")
                                    setForm({ ...form, [key]: v })
                                  }}
                                  placeholder={placeholder[0]}
                                  className={`h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                                    Icon ? "pl-8" : ""
                                  }`}
                                />
                                {Icon && (
                                  <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                )}
                              </div>
                            ) : (
                              <div className="flex gap-1.5">
                                <input
                                  value={min}
                                  onChange={(e) => {
                                    const v = e.target.value.replace(/[^0-9]/g, "")
                                    setForm({ ...form, [key]: v && max ? `${v}-${max}` : v })
                                  }}
                                  placeholder={placeholder[0]}
                                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                />
                                <input
                                  value={max}
                                  onChange={(e) => {
                                    const v = e.target.value.replace(/[^0-9]/g, "")
                                    setForm({ ...form, [key]: min && v ? `${min}-${v}` : v })
                                  }}
                                  placeholder={placeholder[1]}
                                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium capitalize">{t("form_location")}</label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <MultiSelectDropdown
                            options={LOCATIONS.filter((l) => l !== "Whole Hamburg")}
                            value={form.location}
                            onChange={(v) => setForm({ ...form, location: v })}
                            placeholder={t("form_location")}
                            icon={MapPin}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const individual = LOCATIONS.filter((l) => l !== "Whole Hamburg")
                            const current = form.location
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean)
                            const allSelected = current.length === individual.length
                            setForm({ ...form, location: allSelected ? "" : individual.join(", ") })
                          }}
                          className="h-9 shrink-0 rounded-md border border-input bg-background px-4 text-sm font-medium text-primary transition-colors hover:bg-accent"
                        >
                          {(() => {
                            const individual = LOCATIONS.filter((l) => l !== "Whole Hamburg")
                            const current = form.location
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean)
                            return current.length === individual.length
                              ? t("btn_deselect_all")
                              : t("btn_select_all")
                          })()}
                        </button>
                      </div>
                    </div>
                    {/* <div className="space-y-1.5">
                      <label className="text-sm font-medium capitalize">
                        {t("form_documents")}
                      </label>
                      <MultiSelectDropdown
                        options={DOCUMENTS_OPTIONS}
                        value={form.documents}
                        onChange={(v) => setForm({ ...form, documents: v })}
                        placeholder={t("form_documents")}
                        renderLabel={(doc) => t(DOC_T_KEY[doc])}
                      />
                    </div> */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium capitalize">
                        {t("th_subscription")}
                      </label>
                      <select
                        value={form.subscription}
                        onChange={(e) => {
                          const v = e.target.value
                          const months = SUBSCRIPTION_MONTHS[v] ?? 0
                          const today = new Date().toISOString().slice(0, 10)
                          const end =
                            months > 0
                              ? new Date(new Date().getTime() + months * 30 * 24 * 60 * 60 * 1000)
                                  .toISOString()
                                  .slice(0, 10)
                              : ""
                          setForm({
                            ...form,
                            subscription: v,
                            subscriptionDuration: months,
                            subscriptionStart: months > 0 ? today : "",
                            subscriptionEnd: end,
                          })
                        }}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="">—</option>
                        {SUBSCRIPTION_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {t(SUB_T_KEY[opt])}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium">
                          {t("form_subscription_start")}
                        </label>
                        <input
                          type="date"
                          value={form.subscriptionStart}
                          onChange={(e) => setForm({ ...form, subscriptionStart: e.target.value })}
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium">{t("form_subscription_end")}</label>
                        <input
                          type="date"
                          value={form.subscriptionEnd}
                          onChange={(e) => setForm({ ...form, subscriptionEnd: e.target.value })}
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <label className="text-sm font-medium cursor-pointer">{t("form_enabled")}</label>
                  <Switch
                    checked={form.enabled}
                    onCheckedChange={(checked) =>
                      setForm({
                        ...form,
                        enabled: checked,
                        disabledReason: checked ? "" : form.disabledReason,
                      })
                    }
                  />
                </div>
                {!form.enabled && (
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">
                      {t("form_disabled_reason")}
                    </label>
                    <select
                      value={form.disabledReason}
                      onChange={(e) => setForm({ ...form, disabledReason: e.target.value })}
                      required
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="">—</option>
                      {DISABLED_REASON_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {t(DISABLED_REASON_T_KEY[opt])}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex justify-end gap-3 pt-2">
                  <DialogClose asChild>
                    <button
                      type="button"
                      className="h-9 px-4 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors"
                    >
                      {t("btn_cancel")}
                    </button>
                  </DialogClose>
                  <button
                    type="submit"
                    className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    {t("btn_save")}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">{t("th_id")}</TableHead>
              <TableHead>{t("th_full_name")}</TableHead>
              <TableHead>{t("th_phone")}</TableHead>
              <TableHead>{t("th_folder_link")}</TableHead>
              {isAdmin && <TableHead>{t("th_operator")}</TableHead>}
              <TableHead>{t("th_partnership")}</TableHead>
              <TableHead>{t("th_price")}</TableHead>
              <TableHead>{t("th_rooms")}</TableHead>
              <TableHead>{t("th_sqft")}</TableHead>
              <TableHead>{t("th_location")}</TableHead>
              <TableHead>{t("th_subscription")}</TableHead>
              <TableHead>{t("th_subscription_start")}</TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={() =>
                    setSubscriptionEndSort(
                      subscriptionEndSort === "none"
                        ? "asc"
                        : subscriptionEndSort === "asc"
                          ? "desc"
                          : "none",
                    )
                  }
                  className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  {t("th_subscription_end")}
                  {subscriptionEndSort === "asc" ? (
                    <ChevronUp className="h-3 w-3" />
                  ) : subscriptionEndSort === "desc" ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : null}
                </button>
              </TableHead>
              <TableHead>{t("th_comment")}</TableHead>
              <TableHead className="w-16">{t("th_actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {enabledClients.map((row, i) => (
              <TableRow key={row.id}>
                <TableCell className="font-mono text-xs text-muted-foreground">{row.id}</TableCell>
                <TableCell>
                  <span className="flex items-center gap-2 whitespace-nowrap">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: row.color || getUserColor(row.fullName) }}
                    />
                    <span className={hideSensitive ? "blur-sm select-none" : ""}>
                      {row.fullName}
                    </span>
                  </span>
                </TableCell>
                <TableCell className="font-mono text-sm">
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <span className={hideSensitive ? "blur-sm select-none" : ""}>{row.phone}</span>
                    {row.phone && (
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(row.phone)
                          toast(t("toast_copied"))
                        }}
                        className="h-6 w-6 rounded hover:bg-accent flex items-center justify-center transition-colors shrink-0"
                        title={t("btn_copy")}
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="flex items-center gap-1.5">
                    <a
                      href={row.folderLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      <FolderOpen className="h-3.5 w-3.5" />
                      {t("btn_open")}
                    </a>
                  </span>
                </TableCell>
                {isAdmin && <TableCell>{row.operator}</TableCell>}
                <TableCell className="font-mono text-xs">
                  {getPartnershipDuration(row.createdAt)}
                </TableCell>
                <TableCell>
                  {row.price ? (
                    <span className="text-sm">{"≤ " + row.price + " $"}</span>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {row.rooms ? (
                    <span className="text-sm">{row.rooms.replace("-", " – ")}</span>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {row.sqft ? (
                    <span className="text-sm">{row.sqft.replace("-", " – ") + " m²"}</span>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1.5">
                    {(() => {
                      const locs = row.location
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean)
                      if (locs.length === 0)
                        return <span className="text-muted-foreground text-sm">—</span>
                      return (
                        <span className="inline-flex items-center gap-1.5 text-sm">
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                          {locs.length}/122
                        </span>
                      )
                    })()}
                  </div>
                </TableCell>
                <TableCell>
                  {row.subscription ? (
                    <span className="text-sm">{t(SUB_T_KEY[row.subscription])}</span>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {row.subscription?.toLowerCase() === "premium" ? (
                    <span className="text-sm">Premium</span>
                  ) : (
                    row.subscriptionStart || <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs whitespace-nowrap">
                  {row.subscription?.toLowerCase() === "premium" ? (
                    <span className="text-sm">Premium</span>
                  ) : row.subscriptionEnd ? (
                    <span className="whitespace-nowrap">
                      {row.subscriptionEnd}
                      <span className="text-muted-foreground ml-1 whitespace-nowrap">
                        (
                        {Math.max(
                          0,
                          Math.floor(
                            (new Date(row.subscriptionEnd).getTime() - new Date().getTime()) /
                              (1000 * 60 * 60 * 24),
                          ),
                        )}
                        d)
                      </span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <span
                    className={`text-xs text-muted-foreground max-w-[160px] inline-block truncate ${
                      hideSensitive ? "blur-sm select-none" : ""
                    }`}
                    title={hideSensitive ? undefined : row.comment || ""}
                  >
                    {row.comment || "—"}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openComment(row.id)}
                      className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
                      title={t("tip_comment")}
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => openProfiles(row.id)}
                      className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
                      title={t("tip_generate_profiles")}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => openPersonal(row.id)}
                      className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors relative"
                      title={t("tip_personal_data")}
                    >
                      <User className="h-3.5 w-3.5" />
                      {hasIncompletePersonalData(row) && (
                        <span className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-red-500 rounded-full border-2 border-background" />
                      )}
                    </button>
                    <button
                      onClick={() => openEdit(row.id)}
                      className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
                      title={t("tip_edit")}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {disabledClients.length > 0 && (
        <div className="rounded-lg border bg-card">
          <div className="px-4 py-3 border-b">
            <h3 className="text-sm font-medium text-muted-foreground">
              {t("th_disabled_clients")} ({disabledClients.length})
            </h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">{t("th_id")}</TableHead>
                <TableHead>{t("th_full_name")}</TableHead>
                {isAdmin && <TableHead>{t("th_operator")}</TableHead>}
                <TableHead>{t("th_reason")}</TableHead>
                <TableHead className="w-16">{t("th_actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {disabledClients.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {row.id}
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-2 whitespace-nowrap">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full shrink-0 opacity-50"
                        style={{ backgroundColor: row.color || getUserColor(row.fullName) }}
                      />
                      <span className={hideSensitive ? "blur-sm select-none" : ""}>
                        {row.fullName}
                      </span>
                    </span>
                  </TableCell>
                  {isAdmin && <TableCell className="text-sm">{row.operator}</TableCell>}
                  <TableCell className="text-sm">
                    {row.disabledReason ? (
                      t(DISABLED_REASON_T_KEY[row.disabledReason])
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit(row.id)}
                        className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
                        title={t("tip_edit")}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => updateMut.mutate({ id: row.id, dto: { enabled: true } })}
                        className="h-8 px-3 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors inline-flex items-center gap-1.5"
                        title={t("tip_enable")}
                      >
                        {t("btn_enable")}
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={propImgOpen}
        onOpenChange={(v) => {
          setPropImgOpen(v)
          if (!v) setPropImgZoom(1)
        }}
      >
        <DialogContent className="w-screen max-w-none h-screen max-h-none sm:rounded-none p-3 flex flex-col">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>SAGA requirements</DialogTitle>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPropImgZoom((z) => Math.max(0.25, z - 0.25))}
                className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="text-sm text-muted-foreground min-w-[3rem] text-center">
                {Math.round(propImgZoom * 100)}%
              </span>
              <button
                onClick={() => setPropImgZoom((z) => Math.min(5, z + 0.25))}
                className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPropImgZoom(1)}
                className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </div>
          </DialogHeader>
          <div
            className="flex-1 overflow-auto rounded-md"
            onWheel={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault()
                setPropImgZoom((z) => Math.min(5, Math.max(0.25, z + (e.deltaY > 0 ? -0.1 : 0.1))))
              }
            }}
          >
            <img
              src={propImg}
              alt="SAGA requirements"
              className="w-full h-auto rounded-md transition-transform duration-150"
              style={{ transform: `scale(${propImgZoom})`, transformOrigin: "top left" }}
              draggable={false}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={commentOpen}
        onOpenChange={(v) => {
          if (!v) {
            setCommentOpen(false)
            setCommentClientId(null)
            setCommentText("")
          } else {
            setCommentOpen(true)
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("dialog_client_comment")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {data.find((c) => c.id === commentClientId)?.fullName}
            </p>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={t("placeholder_add_comment")}
              rows={5}
              maxLength={2000}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
            />
            <p className="text-xs text-muted-foreground text-right">{commentText.length}/2000</p>
            <div className="flex justify-end gap-3 pt-2">
              <DialogClose asChild>
                <button
                  type="button"
                  className="h-9 px-4 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors"
                >
                  {t("btn_cancel")}
                </button>
              </DialogClose>
              <button
                onClick={handleCommentSubmit}
                className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                {t("btn_save")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={profilesOpen}
        onOpenChange={(v) => {
          if (!v) {
            setProfilesOpen(false)
            setProfilesClientId(null)
            setProfilesData([])
          } else {
            setProfilesOpen(true)
          }
        }}
      >
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              {t("dialog_generate_profiles")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {data.find((c) => c.id === profilesClientId)?.fullName}
            </p>
            {!profilesLoading && profilesData.length === 0 && (
              <p className="text-sm text-amber-500">{t("no_comment_warning")}</p>
            )}
            {profilesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  <p className="text-sm text-muted-foreground">{t("generating_profiles")}</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[70vh] overflow-y-auto pr-1">
                {profilesData.map((profile, idx) => (
                  <div key={idx} className="rounded-lg border border-input bg-card p-4 space-y-3">
                    <h3 className="font-medium text-sm truncate">{profile.name}</h3>

                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("label_max_rent")}:</span>
                        <span className="font-medium">€{profile.maxRent ?? "—"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("label_min_rooms")}:</span>
                        <span className="font-medium">{profile.minRooms ?? "—"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("label_max_rooms")}:</span>
                        <span className="font-medium">{profile.maxRooms ?? "—"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("label_size")}:</span>
                        <span className="font-medium">
                          {profile.minSize ?? "—"}–{profile.maxSize ?? "—"}
                        </span>
                      </div>
                    </div>

                    {profile.location?.length > 0 && (
                      <div className="text-xs space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground font-medium">
                            Districts ({profile.location.length}):
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(profile.location.join(", "))
                              toast(t("btn_copy"), "success")
                            }}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            title={t("btn_copy")}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <textarea
                          readOnly
                          value={profile.location.join(", ")}
                          rows={3}
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-[11px] font-mono resize-none focus:outline-none"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <DialogClose asChild>
                <button
                  type="button"
                  className="h-9 px-4 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors"
                >
                  {t("btn_close")}
                </button>
              </DialogClose>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={personalOpen}
        onOpenChange={(v) => {
          if (!v) {
            setPersonalOpen(false)
            setPersonalClientId(null)
          } else {
            setPersonalOpen(true)
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {t("dialog_personal_data")}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const client = data.find((c) => c.id === personalClientId)
            if (!client) return null
            const inputCls =
              "h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            return (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{client.fullName}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_sex")}</label>
                    <select
                      value={personalForm.sex ?? ""}
                      onChange={(e) =>
                        setPersonalForm({
                          ...personalForm,
                          sex: (e.target.value || null) as "MALE" | "FEMALE" | null,
                        })
                      }
                      className={inputCls}
                    >
                      <option value="">—</option>
                      <option value="MALE">{t("sex_male")}</option>
                      <option value="FEMALE">{t("sex_female")}</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_birth_name")}</label>
                    <input
                      value={personalForm.birthName}
                      onChange={(e) =>
                        setPersonalForm({ ...personalForm, birthName: e.target.value })
                      }
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_birth_place_city")}</label>
                    <input
                      value={personalForm.birthPlaceCity}
                      onChange={(e) =>
                        setPersonalForm({ ...personalForm, birthPlaceCity: e.target.value })
                      }
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_birth_place_country")}</label>
                    <input
                      value={personalForm.birthPlaceCountry}
                      onChange={(e) =>
                        setPersonalForm({ ...personalForm, birthPlaceCountry: e.target.value })
                      }
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_profession")}</label>
                    <input
                      value={personalForm.profession}
                      onChange={(e) =>
                        setPersonalForm({ ...personalForm, profession: e.target.value })
                      }
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_employer")}</label>
                    <input
                      value={personalForm.employer}
                      onChange={(e) =>
                        setPersonalForm({ ...personalForm, employer: e.target.value })
                      }
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_monthly_net_income")}</label>
                    <div className="relative">
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={personalForm.monthlyNetIncome ?? ""}
                        onChange={(e) =>
                          setPersonalForm({
                            ...personalForm,
                            monthlyNetIncome: e.target.value === "" ? null : Number(e.target.value),
                          })
                        }
                        className={`${inputCls} pr-8`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        €
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_street")}</label>
                    <input
                      value={personalForm.street}
                      onChange={(e) => setPersonalForm({ ...personalForm, street: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_house_number")}</label>
                    <input
                      value={personalForm.houseNumber}
                      onChange={(e) =>
                        setPersonalForm({ ...personalForm, houseNumber: e.target.value })
                      }
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium uppercase">{t("form_plz")}</label>
                    <input
                      value={personalForm.plz}
                      onChange={(e) => setPersonalForm({ ...personalForm, plz: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_city")}</label>
                    <input
                      value={personalForm.city}
                      onChange={(e) => setPersonalForm({ ...personalForm, city: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_country")}</label>
                    <input
                      value={personalForm.country}
                      onChange={(e) =>
                        setPersonalForm({ ...personalForm, country: e.target.value })
                      }
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <label className="text-sm font-medium cursor-pointer">
                    {t("form_additional_tenant_info")}
                  </label>
                  <Switch
                    checked={personalForm.additionalTenantInfo}
                    onCheckedChange={(checked) =>
                      setPersonalForm((prev) => ({ ...prev, additionalTenantInfo: checked }))
                    }
                  />
                </div>

                {/* Co-Tenant Section */}
                {personalForm.additionalTenantInfo && (
                  <div className="mt-4 p-3 border border-border rounded-md space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold">
                        {t("form_additional_persons_co_tenant")}
                      </h4>
                      <button
                        type="button"
                        onClick={() =>
                          setPersonalForm({
                            ...personalForm,
                            additionalPersons: [
                              ...personalForm.additionalPersons,
                              {
                                type: "CO_TENANT",
                                firstName: "",
                                lastName: "",
                                birthday: "",
                                salutation: "",
                                birthName: "",
                                birthPlaceCity: "",
                                street: "",
                                houseNumber: "",
                                plz: "",
                                city: "",
                                country: "",
                                email: "",
                                phone: "",
                                profession: "",
                                employer: "",
                                monthlyNetIncome: null,
                              },
                            ],
                          })
                        }
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {t("form_add_person")}
                      </button>
                    </div>
                    {personalForm.additionalPersons
                      .filter((p) => p.type === "CO_TENANT")
                      .map((person, idx) => {
                        const realIdx = personalForm.additionalPersons.findIndex(
                          (p) =>
                            p.type === "CO_TENANT" &&
                            p.firstName === person.firstName &&
                            p.lastName === person.lastName &&
                            p.email === person.email,
                        )
                        return (
                          <div key={realIdx} className="p-3 bg-muted rounded-md space-y-3">
                            <div className="flex items-center justify-between">
                              <h5 className="text-xs font-medium text-muted-foreground">
                                Mieter/in {idx + 1}
                              </h5>
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = personalForm.additionalPersons.filter(
                                    (_, i) => i !== realIdx,
                                  )
                                  setPersonalForm({ ...personalForm, additionalPersons: updated })
                                }}
                                className="text-xs text-red-500 hover:text-red-700"
                              >
                                ✕
                              </button>
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                              <div className="space-y-1">
                                <label className="text-xs font-medium">
                                  {t("form_salutation")}
                                </label>
                                <select
                                  value={person.salutation}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      salutation: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                >
                                  <option value="">—</option>
                                  <option value="MALE">{t("sex_male")}</option>
                                  <option value="FEMALE">{t("sex_female")}</option>
                                </select>
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium">
                                  {t("form_first_name")} *
                                </label>
                                <input
                                  value={person.firstName}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      firstName: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="Vorname"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium">
                                  {t("form_last_name")} *
                                </label>
                                <input
                                  value={person.lastName}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      lastName: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="Nachname"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                              <div className="space-y-1">
                                <label className="text-xs font-medium">
                                  {t("form_birth_name")}
                                </label>
                                <input
                                  value={person.birthName}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      birthName: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="Geburtsnamen eingeben"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium">
                                  {t("form_birth_place_city")} *
                                </label>
                                <input
                                  value={person.birthPlaceCity}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      birthPlaceCity: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="Geburtsort eingeben"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium">
                                  {t("form_birthday")} *
                                </label>
                                <input
                                  value={person.birthday}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      birthday: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="TT.MM.JJJJ"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                              <div className="space-y-1">
                                <label className="text-xs font-medium">{t("form_street")} *</label>
                                <input
                                  value={person.street}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      street: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="Straße eingeben"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium">
                                  {t("form_house_number")} *
                                </label>
                                <input
                                  value={person.houseNumber}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      houseNumber: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="Nr."
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium">{t("form_plz")} *</label>
                                <input
                                  value={person.plz}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = { ...updated[realIdx], plz: e.target.value }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="PLZ"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium">{t("form_city")} *</label>
                                <input
                                  value={person.city}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = { ...updated[realIdx], city: e.target.value }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="Stadt eingeben"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                              <div className="space-y-1">
                                <label className="text-xs font-medium">{t("form_country")}</label>
                                <input
                                  value={person.country}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      country: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="Land"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium">{t("form_email")} *</label>
                                <input
                                  type="email"
                                  value={person.email}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      email: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="email@beispiel.de"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium">{t("form_phone")} *</label>
                                <input
                                  value={person.phone}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      phone: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="+49 01512 3456789"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                              <div className="space-y-1">
                                <label className="text-xs font-medium">
                                  {t("form_profession")}
                                </label>
                                <input
                                  value={person.profession}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      profession: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="Beruf eingeben"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium">{t("form_employer")}</label>
                                <input
                                  value={person.employer}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      employer: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                  placeholder="Arbeitgeber eingeben"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium">
                                  {t("form_monthly_net_income")}
                                </label>
                                <div className="relative">
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={person.monthlyNetIncome ?? ""}
                                    onChange={(e) => {
                                      const updated = [...personalForm.additionalPersons]
                                      updated[realIdx] = {
                                        ...updated[realIdx],
                                        monthlyNetIncome:
                                          e.target.value === "" ? null : Number(e.target.value),
                                      }
                                      setPersonalForm({
                                        ...personalForm,
                                        additionalPersons: updated,
                                      })
                                    }}
                                    className={`${inputCls} pr-8`}
                                    placeholder="Nettoeinkommen eingeben"
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                                    €
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    {personalForm.additionalPersons.filter((p) => p.type === "CO_TENANT").length ===
                      0 && (
                      <p className="text-xs text-muted-foreground">Keine Personen hinzugefügt</p>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between pt-1">
                  <label className="text-sm font-medium cursor-pointer">
                    {t("form_additional_adults")}
                  </label>
                  <Switch
                    checked={personalForm.additionalAdults}
                    onCheckedChange={(checked) =>
                      setPersonalForm((prev) => ({ ...prev, additionalAdults: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between pt-1">
                  <label className="text-sm font-medium cursor-pointer">
                    {t("form_additional_minors")}
                  </label>
                  <Switch
                    checked={personalForm.additionalMinors}
                    onCheckedChange={(checked) =>
                      setPersonalForm((prev) => ({ ...prev, additionalMinors: checked }))
                    }
                  />
                </div>

                {/* Additional Adults Section */}
                {personalForm.additionalAdults && (
                  <div className="mt-4 p-3 border border-border rounded-md space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold">
                        {t("form_additional_persons_adult")}
                      </h4>
                      <button
                        type="button"
                        onClick={() =>
                          setPersonalForm({
                            ...personalForm,
                            additionalPersons: [
                              ...personalForm.additionalPersons,
                              {
                                type: "ADULT",
                                firstName: "",
                                lastName: "",
                                birthday: "",
                                salutation: "",
                                birthName: "",
                                birthPlaceCity: "",
                                street: "",
                                houseNumber: "",
                                plz: "",
                                city: "",
                                country: "",
                                email: "",
                                phone: "",
                                profession: "",
                                employer: "",
                                monthlyNetIncome: null,
                              },
                            ],
                          })
                        }
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {t("form_add_person")}
                      </button>
                    </div>
                    {personalForm.additionalPersons
                      .filter((p) => p.type === "ADULT")
                      .map((person, idx) => {
                        const realIdx = personalForm.additionalPersons.findIndex(
                          (p) =>
                            p.type === "ADULT" &&
                            p.firstName === person.firstName &&
                            p.lastName === person.lastName,
                        )
                        return (
                          <div
                            key={realIdx}
                            className="grid grid-cols-3 gap-2 items-end p-2 bg-muted rounded"
                          >
                            <div className="space-y-1">
                              <label className="text-xs font-medium">{t("form_first_name")}</label>
                              <input
                                value={person.firstName}
                                onChange={(e) => {
                                  const updated = [...personalForm.additionalPersons]
                                  updated[realIdx] = {
                                    ...updated[realIdx],
                                    firstName: e.target.value,
                                  }
                                  setPersonalForm({ ...personalForm, additionalPersons: updated })
                                }}
                                className={inputCls}
                                placeholder="Vorname"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium">{t("form_last_name")}</label>
                              <input
                                value={person.lastName}
                                onChange={(e) => {
                                  const updated = [...personalForm.additionalPersons]
                                  updated[realIdx] = {
                                    ...updated[realIdx],
                                    lastName: e.target.value,
                                  }
                                  setPersonalForm({ ...personalForm, additionalPersons: updated })
                                }}
                                className={inputCls}
                                placeholder="Nachname"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium">{t("form_birthday")}</label>
                              <div className="flex gap-1">
                                <input
                                  type="date"
                                  value={person.birthday}
                                  onChange={(e) => {
                                    const updated = [...personalForm.additionalPersons]
                                    updated[realIdx] = {
                                      ...updated[realIdx],
                                      birthday: e.target.value,
                                    }
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className={inputCls}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = personalForm.additionalPersons.filter(
                                      (_, i) => i !== realIdx,
                                    )
                                    setPersonalForm({ ...personalForm, additionalPersons: updated })
                                  }}
                                  className="h-9 px-2 text-red-500 hover:text-red-700"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    {personalForm.additionalPersons.filter((p) => p.type === "ADULT").length ===
                      0 && (
                      <p className="text-xs text-muted-foreground">Keine Personen hinzugefügt</p>
                    )}
                  </div>
                )}

                {/* Additional Minors Section */}
                {personalForm.additionalMinors && (
                  <div className="mt-4 p-3 border border-border rounded-md space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold">
                        {t("form_additional_persons_minor")}
                      </h4>
                      <button
                        type="button"
                        onClick={() =>
                          setPersonalForm({
                            ...personalForm,
                            additionalPersons: [
                              ...personalForm.additionalPersons,
                              {
                                type: "MINOR",
                                firstName: "",
                                lastName: "",
                                birthday: "",
                                salutation: "",
                                birthName: "",
                                birthPlaceCity: "",
                                street: "",
                                houseNumber: "",
                                plz: "",
                                city: "",
                                country: "",
                                email: "",
                                phone: "",
                                profession: "",
                                employer: "",
                                monthlyNetIncome: null,
                              },
                            ],
                          })
                        }
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {t("form_add_person")}
                      </button>
                    </div>
                    {personalForm.additionalPersons
                      .filter((p) => p.type === "MINOR")
                      .map((person, idx) => {
                        const realIdx = personalForm.additionalPersons.findIndex(
                          (p) =>
                            p.type === "MINOR" &&
                            p.firstName === person.firstName &&
                            p.lastName === person.lastName,
                        )
                        return (
                          <div
                            key={realIdx}
                            className="grid grid-cols-3 gap-2 items-end p-2 bg-muted rounded"
                          >
                            <div className="space-y-1">
                              <label className="text-xs font-medium">{t("form_first_name")}</label>
                              <input
                                value={person.firstName}
                                onChange={(e) => {
                                  const updated = [...personalForm.additionalPersons]
                                  updated[realIdx] = {
                                    ...updated[realIdx],
                                    firstName: e.target.value,
                                  }
                                  setPersonalForm({ ...personalForm, additionalPersons: updated })
                                }}
                                className={inputCls}
                                placeholder="Vorname"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium">{t("form_last_name")}</label>
                              <input
                                value={person.lastName}
                                onChange={(e) => {
                                  const updated = [...personalForm.additionalPersons]
                                  updated[realIdx] = {
                                    ...updated[realIdx],
                                    lastName: e.target.value,
                                  }
                                  setPersonalForm({ ...personalForm, additionalPersons: updated })
                                }}
                                className={inputCls}
                                placeholder="Nachname"
                              />
                            </div>
                            <div className="flex items-end">
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = personalForm.additionalPersons.filter(
                                    (_, i) => i !== realIdx,
                                  )
                                  setPersonalForm({ ...personalForm, additionalPersons: updated })
                                }}
                                className="h-9 px-2 text-red-500 hover:text-red-700"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    {personalForm.additionalPersons.filter((p) => p.type === "MINOR").length ===
                      0 && (
                      <p className="text-xs text-muted-foreground">Keine Personen hinzugefügt</p>
                    )}
                  </div>
                )}
              </div>
            )
          })()}
          <div className="flex justify-end gap-3 pt-2">
            <DialogClose asChild>
              <button
                type="button"
                className="h-9 px-4 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors"
              >
                {t("btn_cancel")}
              </button>
            </DialogClose>
            <button
              onClick={handlePersonalSubmit}
              className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              {t("btn_save")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Clients
